// ---------------------------------------------------------------------------
// DSS-10: Visual box model editor — interactive padding + margin diagram
// ---------------------------------------------------------------------------
import { useState, useCallback, useId } from "react";
import { Square, Link, Unlink } from "lucide-react";

// ── Types ──
export type SpacingUnit = "px" | "em" | "rem" | "%" | "vw" | "vh";

export interface SpacingConfig {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
  readonly unit: SpacingUnit;
}

interface BoxModelEditorProps {
  readonly padding: SpacingConfig;
  readonly margin: SpacingConfig;
  readonly onPaddingChange: (config: SpacingConfig) => void;
  readonly onMarginChange: (config: SpacingConfig) => void;
}

// ── Constants ──
const SPACING_UNITS: SpacingUnit[] = ["px", "em", "rem", "%", "vw", "vh"];
const MIN_MARGIN = -200;
const MAX_VALUE = 500;
const MIN_PADDING = 0;

type Side = "top" | "right" | "bottom" | "left";
const SIDES: Side[] = ["top", "right", "bottom", "left"];

// ── NaN guard ──
function safeNum(raw: string, fallback: number): number {
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function clamp(val: number, min: number, max: number): number {
  if (Number.isNaN(val)) return min;
  return Math.min(Math.max(val, min), max);
}

// ── Component ──
export default function BoxModelEditor({
  padding,
  margin,
  onPaddingChange,
  onMarginChange,
}: BoxModelEditorProps) {
  const uid = useId();
  const [isMarginLinked, setIsMarginLinked] = useState(false);
  const [isPaddingLinked, setIsPaddingLinked] = useState(false);
  const [editingSide, setEditingSide] = useState<{
    layer: "padding" | "margin";
    side: Side;
  } | null>(null);

  const updateMargin = useCallback(
    (side: Side, raw: string) => {
      const val = clamp(safeNum(raw, margin[side]), MIN_MARGIN, MAX_VALUE);
      if (isMarginLinked) {
        onMarginChange({ ...margin, top: val, right: val, bottom: val, left: val });
      } else {
        onMarginChange({ ...margin, [side]: val });
      }
    },
    [margin, isMarginLinked, onMarginChange],
  );

  const updatePadding = useCallback(
    (side: Side, raw: string) => {
      const val = clamp(safeNum(raw, padding[side]), MIN_PADDING, MAX_VALUE);
      if (isPaddingLinked) {
        onPaddingChange({ ...padding, top: val, right: val, bottom: val, left: val });
      } else {
        onPaddingChange({ ...padding, [side]: val });
      }
    },
    [padding, isPaddingLinked, onPaddingChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Square className="h-3.5 w-3.5" />
        Box Model
      </div>

      {/* Interactive diagram */}
      <div className="relative flex items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-2">
        {/* Margin layer */}
        <div
          className="relative flex items-center justify-center rounded-md border-2 border-dashed border-orange-300/60 bg-orange-50/30 dark:bg-orange-900/10"
          style={{ padding: "20px 28px" }}
        >
          <span className="absolute left-1 top-0.5 text-[8px] font-medium text-orange-400">
            margin
          </span>
          {/* Margin side inputs */}
          {SIDES.map((side) => (
            <SideInput
              key={`m-${side}`}
              side={side}
              layer="margin"
              value={margin[side]}
              isEditing={editingSide?.layer === "margin" && editingSide.side === side}
              onStartEdit={() => setEditingSide({ layer: "margin", side })}
              onEndEdit={() => setEditingSide(null)}
              onChange={(raw) => updateMargin(side, raw)}
            />
          ))}

          {/* Padding layer */}
          <div
            className="relative flex items-center justify-center rounded border-2 border-dashed border-green-400/60 bg-green-50/30 dark:bg-green-900/10"
            style={{ padding: "20px 28px" }}
          >
            <span className="absolute left-1 top-0.5 text-[8px] font-medium text-green-500">
              padding
            </span>
            {/* Padding side inputs */}
            {SIDES.map((side) => (
              <SideInput
                key={`p-${side}`}
                side={side}
                layer="padding"
                value={padding[side]}
                isEditing={editingSide?.layer === "padding" && editingSide.side === side}
                onStartEdit={() => setEditingSide({ layer: "padding", side })}
                onEndEdit={() => setEditingSide(null)}
                onChange={(raw) => updatePadding(side, raw)}
              />
            ))}

            {/* Content box */}
            <div className="flex h-8 w-16 items-center justify-center rounded bg-[var(--color-primary)]/10 text-[9px] font-medium text-[var(--color-primary)]">
              content
            </div>
          </div>
        </div>
      </div>

      {/* Margin controls */}
      <SpacingGroup
        uid={`${uid}-m`}
        label="Margin"
        config={margin}
        isLinked={isMarginLinked}
        onToggleLink={() => setIsMarginLinked(!isMarginLinked)}
        onSideChange={updateMargin}
        onUnitChange={(unit) => onMarginChange({ ...margin, unit })}
        min={MIN_MARGIN}
      />

      {/* Padding controls */}
      <SpacingGroup
        uid={`${uid}-p`}
        label="Padding"
        config={padding}
        isLinked={isPaddingLinked}
        onToggleLink={() => setIsPaddingLinked(!isPaddingLinked)}
        onSideChange={updatePadding}
        onUnitChange={(unit) => onPaddingChange({ ...padding, unit })}
        min={MIN_PADDING}
      />

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateBoxModelCss(padding, margin)}
        </code>
      </div>
    </div>
  );
}

// ── Side input on diagram ──
const SIDE_POSITIONS: Record<Side, string> = {
  top: "absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full",
  right: "absolute -right-1 top-1/2 -translate-y-1/2 translate-x-full",
  bottom: "absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full",
  left: "absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full",
};

function SideInput({ side, value, isEditing, onStartEdit, onEndEdit, onChange }: {
  readonly side: Side; readonly layer: "padding" | "margin"; readonly value: number;
  readonly isEditing: boolean; readonly onStartEdit: () => void;
  readonly onEndEdit: () => void; readonly onChange: (raw: string) => void;
}) {
  if (isEditing) {
    return (
      <input
        type="number"
        autoFocus
        defaultValue={value}
        onBlur={(e) => {
          onChange(e.target.value);
          onEndEdit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onChange((e.target as HTMLInputElement).value);
            onEndEdit();
          }
        }}
        className={`${SIDE_POSITIONS[side]} h-5 w-10 rounded border border-[var(--color-primary)] bg-[var(--color-card)] text-center text-[9px] tabular-nums text-[var(--color-foreground)] outline-none`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onStartEdit}
      className={`${SIDE_POSITIONS[side]} cursor-pointer rounded px-1 text-[9px] tabular-nums text-[var(--color-muted-foreground)] hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)]`}
    >
      {value}
    </button>
  );
}

// ── Spacing group (4 inputs + link + unit) ──
function SpacingGroup({ uid, label, config, isLinked, onToggleLink, onSideChange, onUnitChange, min }: {
  readonly uid: string; readonly label: string; readonly config: SpacingConfig;
  readonly isLinked: boolean; readonly onToggleLink: () => void;
  readonly onSideChange: (side: Side, raw: string) => void;
  readonly onUnitChange: (unit: SpacingUnit) => void; readonly min: number;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[var(--color-foreground)]">{label}</span>
        <div className="flex items-center gap-2">
          <select
            value={config.unit}
            onChange={(e) => onUnitChange(e.target.value as SpacingUnit)}
            className="h-6 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1 text-[10px] text-[var(--color-muted-foreground)]"
          >
            {SPACING_UNITS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onToggleLink}
            title={isLinked ? "Unlink sides" : "Link all sides"}
            className={[
              "rounded p-1",
              isLinked
                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
            ].join(" ")}
          >
            {isLinked ? <Link className="h-3 w-3" /> : <Unlink className="h-3 w-3" />}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {SIDES.map((side) => (
          <div key={side} className="flex flex-col items-center gap-0.5">
            <label
              htmlFor={`${uid}-${side}`}
              className="text-[8px] uppercase text-[var(--color-muted-foreground)]"
            >
              {side[0]}
            </label>
            <input
              id={`${uid}-${side}`}
              type="number"
              min={min}
              max={MAX_VALUE}
              value={config[side]}
              onChange={(e) => onSideChange(side, e.target.value)}
              className="h-6 w-full rounded border border-[var(--color-border)] bg-[var(--color-card)] text-center text-[10px] tabular-nums text-[var(--color-foreground)]"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CSS generation ──
function formatSpacing(prop: string, c: SpacingConfig): string {
  const u = c.unit;
  const allSame = c.top === c.right && c.right === c.bottom && c.bottom === c.left;
  return allSame
    ? `${prop}: ${c.top}${u};`
    : `${prop}: ${c.top}${u} ${c.right}${u} ${c.bottom}${u} ${c.left}${u};`;
}

export function generateBoxModelCss(padding: SpacingConfig, margin: SpacingConfig): string {
  return `${formatSpacing("padding", padding)}\n${formatSpacing("margin", margin)}`;
}
